import type { Bidhi, BidhiLine, BidhiLineMultipleBidhiLink, ChildBidhi } from '../types';

export type ApiBidhiLine = BidhiLine;

const childAttachmentKey = (c: { id: number; lineId?: number }) => `${c.id}:${c.lineId ?? ''}`;

function readHierarchyIsTitleHidden(src: Record<string, unknown>): boolean {
  if (typeof src.hierarchyIsTitleHidden === 'boolean') return src.hierarchyIsTitleHidden;
  const hierarchy = src.hierarchy;
  if (hierarchy && typeof hierarchy === 'object' && 'isTitleHidden' in hierarchy) {
    return Boolean((hierarchy as Record<string, unknown>).isTitleHidden);
  }
  return false;
}

function normalizeChildBidhiFromApi(raw: unknown): ChildBidhi {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const base = normalizeBidhiFromApi(raw) as ChildBidhi;
  return {
    ...base,
    entireBidhi: Boolean(src.entireBidhi ?? base.entireBidhi),
    selectedBidhiLineIds: Array.isArray(src.selectedBidhiLineIds)
      ? (src.selectedBidhiLineIds as number[])
      : base.selectedBidhiLineIds ?? [],
    lineId: typeof src.lineId === 'number' ? src.lineId : base.lineId,
    startingPosition: typeof src.startingPosition === 'number' ? src.startingPosition : base.startingPosition,
    hierarchyIsTitleHidden: readHierarchyIsTitleHidden(src),
  };
}

/**
 * Normalize bidhi payloads from GET /bidhi, GET /bidhi/roots, etc.
 * - Ensures arrays exist
 * - Merges line.embeddedBidhis into bidhi.children (deduped)
 * - Recursively normalizes nested bidhis
 */
export function normalizeBidhiFromApi(raw: unknown): Bidhi {
  if (!raw || typeof raw !== 'object') {
    return {
      id: 0,
      isTitleHidden: false,
      name: '',
      lines: [],
      tags: [],
      samagriAssociations: [],
      children: [],
    };
  }

  const src = raw as Record<string, unknown>;
  const lines = Array.isArray(src.lines) ? src.lines.map(normalizeBidhiLine) : [];

  const childrenFromApi = Array.isArray(src.children)
    ? (src.children as ChildBidhi[]).map((c) => normalizeChildBidhiFromApi(c))
    : [];

  const childMap = new Map<string, ChildBidhi>();
  childrenFromApi.forEach((c) => childMap.set(childAttachmentKey(c), c));

  lines.forEach((line) => {
    const embedded = (line as ApiBidhiLine).embeddedBidhis;
    if (!embedded?.length) return;
    embedded.forEach((eb) => {
      const normalized = normalizeChildBidhiFromApi(eb);
      if (normalized.lineId == null) {
        normalized.lineId = line.id;
      }
      const key = childAttachmentKey(normalized);
      if (!childMap.has(key)) {
        childMap.set(key, normalized);
      }
    });
  });

  return {
    ...(src as unknown as Bidhi),
    lines,
    tags: Array.isArray(src.tags) ? src.tags : [],
    samagriAssociations: Array.isArray(src.samagriAssociations) ? src.samagriAssociations : [],
    isTitleHidden: Boolean(src.isTitleHidden),
    children: Array.from(childMap.values()),
    granthaReferences: Array.isArray(src.granthaReferences) ? src.granthaReferences : [],
    hyperlinkBidhis: Array.isArray(src.hyperlinkBidhis) ? src.hyperlinkBidhis : [],
    hyperlinkSankalpas: Array.isArray(src.hyperlinkSankalpas) ? src.hyperlinkSankalpas : [],
  };
}

export function normalizeBidhiListFromApi(raw: unknown): Bidhi[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => normalizeBidhiFromApi(item));
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data.map((item) => normalizeBidhiFromApi(item));
    if (Array.isArray(obj.roots)) return obj.roots.map((item) => normalizeBidhiFromApi(item));
    if (Array.isArray(obj.items)) return obj.items.map((item) => normalizeBidhiFromApi(item));
  }
  return [];
}

function normalizeBidhiLine(line: unknown): ApiBidhiLine {
  const src = (line && typeof line === 'object' ? line : {}) as Record<string, unknown>;
  const multipleBidhiLinks = Array.isArray(src.multipleBidhiLinks)
    ? (src.multipleBidhiLinks as BidhiLineMultipleBidhiLink[]).map((link) => ({
        ...link,
        bidhi: link.bidhi ? (normalizeBidhiFromApi(link.bidhi) as Bidhi) : link.bidhi,
      }))
    : [];

  return {
    ...(src as unknown as BidhiLine),
    multipleBidhiLinks,
    embeddedBidhis: Array.isArray(src.embeddedBidhis) ? (src.embeddedBidhis as ChildBidhi[]) : [],
    sankalpaLinks: Array.isArray(src.sankalpaLinks) ? src.sankalpaLinks : [],
    granthaLinks: Array.isArray(src.granthaLinks) ? src.granthaLinks : [],
    bheds: Array.isArray(src.bheds) ? src.bheds : [],
    imageAssociations: Array.isArray(src.imageAssociations) ? src.imageAssociations : [],
  };
}

/** Child bidhis attached to a line — from bidhi.children and line.embeddedBidhis */
export function getLineChildBidhis(bidhiData: Bidhi | ChildBidhi, lineId: number): ChildBidhi[] {
  const map = new Map<string, ChildBidhi>();

  (bidhiData.children || []).forEach((child) => {
    if (child.lineId === lineId) {
      map.set(childAttachmentKey(child), child);
    }
  });

  const line = bidhiData.lines?.find((l) => l.id === lineId) as ApiBidhiLine | undefined;
  (line?.embeddedBidhis || []).forEach((embedded) => {
    const key = childAttachmentKey(embedded);
    if (!map.has(key)) {
      map.set(key, embedded);
    }
  });

  return Array.from(map.values());
}
